import { ButtonBase, styled } from '@mui/material';
import { useCallback, useState } from 'react';

const Root = styled(ButtonBase)(() => ({
  position: 'relative',
  padding: 0,
  margin: 0,
  outline: 0,
}));

const Outer = styled('div')(({ theme }) => ({
  borderRadius: 6,
  border: `1px solid #a9b6bc`,
  backgroundColor: '#ccd4d6',
  height: '100%',
  width: '100%',
  padding: '2px 3px',
  transition: 'border-color .15s, background .15s',
  '[data-hover="true"] &': {
    borderColor: '#a3b1b8',
    backgroundColor: '#b6c1c4',
  },
  '[data-active="true"] &': {
    borderColor: theme.palette.shamrock[500],
    backgroundColor: theme.palette.shamrock['400Base'],
  },
  '[data-variant="active"] &': {
    borderColor: theme.palette.shamrock[600],
    backgroundColor: theme.palette.shamrock['400Base'],
  },
  '.Mui-disabled &': {
    backgroundColor: '#DDE2E4',
    borderColor: '#BAC5C9',
  },
}));

const InnerBorder = styled('div')(({ theme }) => ({
  padding: '1px',
  height: '100%',
  width: '100%',
  borderRadius: 4,
  background: 'linear-gradient(to bottom, #F7F7F7, #E2E7E9)',
  boxShadow:
    '0px 34px 9px rgba(32,44,49,0), 0px 22px 9px rgba(32,44,49,0.01), 0px 12px 7px rgba(32,44,49,0.03), 0px 5px 5px rgba(32,44,49,0.04), 0px 1px 3px rgba(32,44,49,0.05)',
  '[data-hover="true"] &': {
    background: 'linear-gradient(to bottom, #ffffff, #e8edef)',
  },
  '[data-active="true"] &': {
    background: 'linear-gradient(to bottom, #eff1f1, #f1f3f3)',
  },
  '[data-variant="active"] &': {
    boxShadow: `0px 2px 15px ${theme.palette.shamrock['400Base']}`,
  },
  '.Mui-disabled &': {
    backgroundImage: 'linear-gradient(to bottom, #EBEBEB, #E2E7E9)',
    color: '#b6c1c3',
  },
}));

const Inner = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  borderRadius: 3,
  backgroundImage: 'linear-gradient(to bottom, #e4e7e7, #eff1f1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#394b57',
  fontSize: 14,
  fontWeight: 400,
  textTransform: 'none',
  userSelect: 'none',
  fontFamily: 'Inter',
  '[data-hover="true"] &': {
    backgroundImage: 'linear-gradient(to bottom, #eff1f1, #f1f3f3)',
    color: '#3c5462',
  },
  '[data-variant="active"] &': {
    color: theme.palette.shamrock['600'],
  },
  '.Mui-disabled &': {
    backgroundImage: 'linear-gradient(to bottom, #d3d9d9, #e4e7e7)',
    color: '#b6c1c3',
  },
}));

export const ButtonStd = ({
  children,
  disabled,
  variant = 'default',
  width = '200px',
  height = '32px',
  ...props
}) => {
  const [hovered, setHovered] = useState(false);
  const [active, setActive] = useState(false);
  const handleEnter = useCallback(() => {
    if (!disabled) setHovered(true);
  }, [disabled]);
  const handleLeave = useCallback(() => {
    setHovered(false);
    setActive(false);
  }, []);
  const handleDown = useCallback(() => {
    if (!disabled) setActive(true);
  }, [disabled]);
  const handleUp = useCallback(() => {
    setActive(false);
  }, []);

  return (
    <Root
      data-variant={variant}
      disabled={disabled}
      data-hover={hovered ? 'true' : 'false'}
      data-active={active ? 'true' : 'false'}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onMouseDown={handleDown}
      onMouseUp={handleUp}
      disableRipple
      sx={{
        width,
        height,
        ...props.sx,
      }}
      {...props}
    >
      <Outer>
        <InnerBorder>
          <Inner>{children}</Inner>
        </InnerBorder>
      </Outer>
    </Root>
  );
};
